import { motion, AnimatePresence } from "framer-motion";
import React, {
  useEffect,
  useState,
  useRef,
  useLayoutEffect,
  useCallback,
  useMemo,
  lazy,
  Suspense,
} from "react";
import {
  getAllConnectedNodeEdgesTypes,
  getGraphEdges,
  createNode,
  createEdge,
  deleteNode,
  deleteEdge,
  updateNode,
  updateEdge,
  getTeamGraphs,
  getTeamMemberGraph,
  getConnectedNodeByType,
  getConnectedEdgeByType,
  getConnectedNodeByName,
  getConnectedEdgeByName,
  searchGraph,
  getGlobalGraph,
  getGraphViews,
  deleteGraphView,
  getGraphSources,
  getFilteredGraphView,
  favoriteGraphView,
  unfavoriteGraphView,
  getGraphCounts,
} from "../../../api/graph";

import LoadingSpinner from "../../Universal/LoadingSpinner";
import { useRightSidebar } from "../../contexts/RightSidebarContext";
import { useLayout } from "../../contexts/LayoutContext";
import GraphTutorial from "./GraphTutorial";
import { X } from "lucide-react";

import { THEMES, PANEL_KEYFRAMES } from "./config/graphThemes";
import { safeIcon, NODE_TYPE_OPTIONS } from "./config/graphIcons";
import { CIRCLE_RADIUS } from "./config/graphConstants";
import {
  FloatingPanel,
  ModalInput,
  ModalActions,
  ModalDropdown,
  ModalTextarea,
  ModalToggle,
} from "./components/GraphModals";
import useDark from "./hooks/useDark";
import GraphBottomBar from "./components/GraphBottomBar";
import StatsPanel from "./components/StatisticsPanel";
import LegendPanel from "./components/LegendPanel";
import CreateEditViewModal from "./components/CreateEditViewModal";

// ── Lazy-loaded renderers (separate bundles) ──────────────────────────────
const GraphRenderer2D = lazy(() => import("./components/GraphRenderer2D"));
const GraphRenderer3D = lazy(() => import("./components/GraphRenderer3D"));

const MainGraph = () => {
  const dark = useDark();
  const t = dark ? THEMES.dark : THEMES.light;

  const datePickerTheme = useMemo(
    () =>
      dark
        ? {
            textPrimary: "rgba(226,232,240,0.9)",
            textSecondary: "rgba(148,163,184,0.7)",
            textMuted: "rgba(148,163,184,0.4)",
            inputBg: "rgba(17,24,39,0.60)",
            inputBorder: "rgba(14,165,233,0.12)",
            activeBg: "rgba(14,165,233,0.15)",
            activeBorder: "rgba(14,165,233,0.30)",
            activeText: "rgba(14,165,233,0.9)",
            hoverBg: "rgba(17,24,39,0.80)",
            hoverBorder: "rgba(56,189,248,0.25)",
            hoverBtnShadow: "0 0 12px rgba(28,157,234,0.15)",
            divider: "rgba(14,165,233,0.15)",
            dropBg: "rgba(11,15,20,0.95)",
            dropBorder: "rgba(14,165,233,0.15)",
            dropShadow:
              "0 8px 32px rgba(0,0,0,0.4), 0 0 1px rgba(14,165,233,0.2)",
            pillBg: "rgba(17,24,39,0.60)",
            pillBorder: "rgba(14,165,233,0.12)",
            clearText: "rgba(248,113,113,0.9)",
            clearBg: "rgba(239,68,68,0.10)",
            clearBgHover: "rgba(239,68,68,0.20)",
            clearBorder: "rgba(239,68,68,0.20)",
          }
        : {
            textPrimary: "rgba(15,23,42,0.9)",
            textSecondary: "rgba(71,85,105,0.8)",
            textMuted: "rgba(148,163,184,0.6)",
            inputBg: "rgba(241,245,249,0.8)",
            inputBorder: "rgba(203,213,225,0.5)",
            activeBg: "rgba(14,165,233,0.10)",
            activeBorder: "rgba(14,165,233,0.30)",
            activeText: "rgba(2,132,199,1)",
            hoverBg: "rgba(241,245,249,1)",
            hoverBorder: "rgba(14,165,233,0.25)",
            hoverBtnShadow: "0 0 10px rgba(14,165,233,0.10)",
            divider: "rgba(203,213,225,0.4)",
            dropBg: "rgba(255,255,255,0.98)",
            dropBorder: "rgba(203,213,225,0.5)",
            dropShadow: "0 8px 24px rgba(0,0,0,0.10), 0 0 1px rgba(0,0,0,0.05)",
            pillBg: "rgba(241,245,249,0.8)",
            pillBorder: "rgba(203,213,225,0.4)",
            clearText: "rgba(220,38,38,0.9)",
            clearBg: "rgba(239,68,68,0.08)",
            clearBgHover: "rgba(239,68,68,0.15)",
            clearBorder: "rgba(239,68,68,0.15)",
          },
    [dark],
  );

  const { isRightOpen } = useRightSidebar();
  const { isLeftOpen, rightWidth, LEFT_WIDTH } = useLayout();

  const [is3D, setIs3D] = useState(true);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchEntry, setSearchEntry] = useState("");
  const [connectedTypes, setConnectedTypes] = useState([]);
  const [filteredResults, setFilteredResults] = useState([]);
  const [searchTitle, setSearchTitle] = useState(null);

  const fgRef = useRef();
  const zoomRef = useRef(1);
  const [nodePosition, setNodePosition] = useState(null);
  const [stopEngine, setStopEngine] = useState(false);
  const needsZoomToFit = useRef(true);
  const [graphData, setGraphData] = useState(null);
  const [backendTotals, setBackendTotals] = useState({ nodes: 0, edges: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [tooltipNode, setTooltipNode] = useState(null);
  const tooltipRafRef = useRef(null);
  const divRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [isGraphEmpty, setIsGraphEmpty] = useState(false);
  const hoveredNodeRef = useRef(null);
  const iconImgCacheRef = useRef(new Map());
  const activeNodeRef = useRef(null);
  const [activeNode, setActiveNode] = useState(null);
  const connectDragRef = useRef(null);
  const [connectDrag, setConnectDrag] = useState(null);
  const suppressClickRef = useRef(false);

  const selectedNodeIdsRef = useRef(new Set());
  const [selectedNodeIds, setSelectedNodeIds] = useState(new Set());
  const selectionBoxRef = useRef(null);
  const [selectionBox, setSelectionBox] = useState(null);
  const lastDragPosRef = useRef(null);

  const [showNewNodeModal, setShowNewNodeModal] = useState(false);
  const [newNodeModalPos, setNewNodeModalPos] = useState({ x: 0, y: 0 });
  const [newNodeSourceNode, setNewNodeSourceNode] = useState(null);
  const [newNodeForm, setNewNodeForm] = useState({
    name: "",
    type: "PERSON",
    relationship: "",
    context: "",
    edgeContext: "",
    isPrivate: false,
  });
  const [isDraggingFromPlus, setIsDraggingFromPlus] = useState(false);
  const cursorGraphPosRef = useRef({ x: 0, y: 0 });
  const [connectTargetNode, setConnectTargetNode] = useState(null);
  const [nodeMenu, setNodeMenu] = useState(null);
  const [showStandaloneModal, setShowStandaloneModal] = useState(false);
  const [standaloneForm, setStandaloneForm] = useState({
    name: "",
    type: "PERSON",
    context: "",
    isPrivate: false,
  });
  const standaloneGraphPosRef = useRef(null);
  const standaloneScreenPosRef = useRef({ x: 0, y: 0 });
  const [pendingConnect, setPendingConnect] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);

  const [barSearch, setBarSearch] = useState("");
  const [searchMatchedNodes, setSearchMatchedNodes] = useState(new Set());
  const [searchMatchedLinks, setSearchMatchedLinks] = useState(new Set());
  const [activeTypeFilter, setActiveTypeFilter] = useState(new Set());
  const [showEdgeLabels, setShowEdgeLabels] = useState(false);
  const [manualOnly, setManualOnly] = useState(false);
  const [hideOrphans, setHideOrphans] = useState(false);
  const [degreeHighlight, setDegreeHighlight] = useState(0);
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);
  const tutorialGraphSnapshotRef = useRef(null);

  const [graphSourceLabel, setGraphSourceLabel] = useState("My Graph");
  const [teamMembers, setTeamMembers] = useState([]);
  const [activeGraphUuid, setActiveGraphUuid] = useState(null);
  const [graphLimit, setGraphLimit] = useState(100);
  const [graphDateFrom, setGraphDateFrom] = useState("");
  const [graphDateTo, setGraphDateTo] = useState("");
  const limitDebounceTimer = useRef(null);
  const graphSourceMeta = useRef({ kind: "my_graph" });

  const [graphFavorites, setGraphFavorites] = useState([]);
  const [customViews, setCustomViews] = useState([]);
  const [showCreateViewModal, setShowCreateViewModal] = useState(false);
  const [editingView, setEditingView] = useState(null);
  const [graphSources, setGraphSources] = useState([]);
  const [graphTargetTypes, setGraphTargetTypes] = useState([]);

  const [toasts, setToasts] = useState([]);
  const toastTimersRef = useRef({});

  useEffect(() => {
    setConnectDrag(null);
    connectDragRef.current = null;
    setNodeMenu(null);
    setSelectionBox(null);
    selectionBoxRef.current = null;
    setConnectTargetNode(null);
    setIsDraggingFromPlus(false);
    needsZoomToFit.current = true;
    setTimeout(() => {
      if (fgRef.current) fgRef.current.zoomToFit?.(400, 60);
    }, 600);
  }, [is3D]);

  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.page === "/graph") {
        tutorialGraphSnapshotRef.current = graphData
          ? { nodes: [...graphData.nodes], links: [...graphData.links] }
          : null;
        setShowTutorial(true);
      }
    };
    window.addEventListener("navbar:start-tutorial", handler);
    return () => window.removeEventListener("navbar:start-tutorial", handler);
  }, [graphData]);

  const fetchBackendCounts = useCallback(() => {
    const meta = graphSourceMeta.current;
    let scope = "",
      userUuid = "",
      scopeValue = "",
      filters = null;
    if (meta.kind === "global") scope = "global";
    else if (meta.kind === "team") {
      scope = "team";
      userUuid = meta.uuid || "";
    } else if (meta.kind === "node" || meta.kind === "node_type") {
      scope = "node_type";
      scopeValue = meta.value || "";
    } else if (meta.kind === "link" || meta.kind === "edge_type") {
      scope = "edge_type";
      scopeValue = meta.value || "";
    } else if (meta.kind === "custom_view" && meta.view?.filters) {
      scope = "custom_view";
      filters = meta.view.filters;
    }
    getGraphCounts(scope, userUuid, scopeValue, filters)
      .then((r) => {
        try {
          const d = typeof r?.data === "string" ? JSON.parse(r.data) : r?.data;
          if (d?.status === 200)
            setBackendTotals({
              nodes: d.total_nodes || 0,
              edges: d.total_edges || 0,
            });
        } catch {}
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    getTeamGraphs()
      .then((res) => {
        try {
          if (res.data?.team) setTeamMembers(res.data.team);
        } catch {}
      })
      .catch(() => {});
  }, []);

  const loadGraphViewsData = useCallback(() => {
    getGraphViews()
      .then((res) => {
        try {
          if (res.data?.favorites) setGraphFavorites(res.data.favorites);
          if (res.data?.views) setCustomViews(res.data.views);
        } catch {}
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadGraphViewsData();
    const handler = () => loadGraphViewsData();
    window.addEventListener("sidebar:refresh-graph-views", handler);
    return () =>
      window.removeEventListener("sidebar:refresh-graph-views", handler);
  }, [loadGraphViewsData]);

  useEffect(() => {
    const handler = () => {
      setEditingView(null);
      setShowCreateViewModal(true);
    };
    window.addEventListener("sidebar:graph-open-create-modal", handler);
    return () =>
      window.removeEventListener("sidebar:graph-open-create-modal", handler);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      const view = e.detail?.view;
      if (!view) return;
      setEditingView(view);
      setShowCreateViewModal(true);
    };
    window.addEventListener("sidebar:graph-open-edit-modal", handler);
    return () =>
      window.removeEventListener("sidebar:graph-open-edit-modal", handler);
  }, []);

  useEffect(() => {
    getGraphSources()
      .then((res) => {
        try {
          if (res.data?.sources) setGraphSources(res.data.sources);
          if (res.data?.target_types)
            setGraphTargetTypes(res.data.target_types);
        } catch {}
      })
      .catch(() => {});
  }, []);

  const pushToast = useCallback((message, undoPayload, type = "connection") => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, message, undoPayload, type }]);
    toastTimersRef.current[id] = setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
      delete toastTimersRef.current[id];
    }, 10000);
    return id;
  }, []);

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    if (toastTimersRef.current[id]) {
      clearTimeout(toastTimersRef.current[id]);
      delete toastTimersRef.current[id];
    }
  }, []);

  const degreeMap = useMemo(() => {
    const dm = {};
    (graphData?.links || []).forEach((l) => {
      const s = typeof l.source === "object" ? l.source.id : l.source;
      const t = typeof l.target === "object" ? l.target.id : l.target;
      dm[s] = (dm[s] || 0) + 1;
      dm[t] = (dm[t] || 0) + 1;
    });
    return dm;
  }, [graphData]);

  const maxDegree = useMemo(() => {
    const vals = Object.values(degreeMap);
    return vals.length ? Math.max(...vals) : 0;
  }, [degreeMap]);

  const typeCounts = useMemo(() => {
    const counts = {};
    (graphData?.nodes || []).forEach((n) => {
      const tp = n.icon || n.group || "UNKNOWN";
      counts[tp] = (counts[tp] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [graphData]);

  const graphStats = useMemo(() => {
    const nodes = graphData?.nodes?.length || 0;
    const edges = graphData?.links?.length || 0;
    const manualNodes = (graphData?.nodes || []).filter(
      (n) => n.group === "manual",
    ).length;
    const manualEdges = (graphData?.links || []).filter((l) => l.manual).length;
    const orphanCount = (graphData?.nodes || []).filter(
      (n) => !degreeMap[n.id],
    ).length;
    return { nodes, edges, manualNodes, manualEdges, orphanCount };
  }, [graphData, degreeMap]);

  const visibleNodeIds = useMemo(() => {
    if (!graphData?.nodes) return new Set();
    let nodes = graphData.nodes;
    if (manualOnly) {
      const manualNodeIds = new Set(
        nodes.filter((n) => n.group === "manual").map((n) => n.id),
      );
      (graphData.links || []).forEach((l) => {
        if (l.manual) {
          const s = typeof l.source === "object" ? l.source.id : l.source;
          const tt = typeof l.target === "object" ? l.target.id : l.target;
          manualNodeIds.add(s);
          manualNodeIds.add(tt);
        }
      });
      nodes = nodes.filter((n) => manualNodeIds.has(n.id));
    }
    if (hideOrphans) nodes = nodes.filter((n) => degreeMap[n.id] > 0);
    if (activeTypeFilter.size > 0)
      nodes = nodes.filter((n) => activeTypeFilter.has(n.icon || n.group));
    return new Set(nodes.map((n) => n.id));
  }, [graphData, activeTypeFilter, manualOnly, hideOrphans, degreeMap]);

  const hasActiveFilter =
    activeTypeFilter.size > 0 ||
    manualOnly ||
    hideOrphans ||
    degreeHighlight > 0 ||
    searchMatchedNodes.size > 0;

  const warmupTicks = useMemo(() => {
    const n = graphData?.nodes?.length || 0;
    return n > 500 ? 100 : n > 200 ? 150 : 200;
  }, [graphData]);

  const handleFgNodeClick = useCallback(
    (node) => {
      if (isDraggingFromPlus || suppressClickRef.current) return;
      setContextMenu(null);
      if (node.group === "manual") {
        activeNodeRef.current = node;
        setActiveNode(node);
        setNodeMenu((prev) => (prev?.node?.id === node.id ? null : { node }));
      } else {
        setNodeMenu(null);
      }
    },
    [isDraggingFromPlus],
  );

  const handleFgLinkClick = useCallback((link, event) => {
    if (link.manual)
      setContextMenu({
        x: event.clientX,
        y: event.clientY,
        item: link,
        kind: "link",
      });
  }, []);

  const handleFgNodeHover = useCallback((node) => {
    hoveredNodeRef.current = node || null;
    if (tooltipRafRef.current) cancelAnimationFrame(tooltipRafRef.current);
    tooltipRafRef.current = requestAnimationFrame(() =>
      setTooltipNode(node || null),
    );
  }, []);

  const linkColorFn = useCallback(
    (link) => {
      const sId =
        typeof link.source === "object" ? link.source.id : link.source;
      const tId =
        typeof link.target === "object" ? link.target.id : link.target;
      const linkVisible =
        !hasActiveFilter ||
        (visibleNodeIds.has(sId) && visibleNodeIds.has(tId));
      if (!linkVisible)
        return dark ? "rgba(14,165,233,0.03)" : "rgba(0,0,0,0.03)";
      const hNode = hoveredNodeRef.current;
      if (
        hNode &&
        (link.source.name === hNode.name || link.target.name === hNode.name)
      )
        return t.link.hovered;
      if (link.manual) return "rgba(251,146,60,0.45)";
      if (searchMatchedLinks.size > 0 && searchMatchedLinks.has(link.id))
        return "rgba(14,165,233,0.65)";
      return t.link.default;
    },
    [dark, hasActiveFilter, visibleNodeIds, searchMatchedLinks, t.link],
  );

  const linkWidthFn = useCallback(
    (link) => {
      const sId =
        typeof link.source === "object" ? link.source.id : link.source;
      const tId =
        typeof link.target === "object" ? link.target.id : link.target;
      const linkVisible =
        !hasActiveFilter ||
        (visibleNodeIds.has(sId) && visibleNodeIds.has(tId));
      return linkVisible
        ? searchMatchedLinks.size > 0 && searchMatchedLinks.has(link.id)
          ? 3
          : 2
        : 0.3;
    },
    [hasActiveFilter, visibleNodeIds, searchMatchedLinks],
  );

  const handleZoomToFit = () => {
    if (fgRef.current) fgRef.current.zoomToFit(0, 60);
  };

  const handleRelayout = () => {
    if (!graphData?.nodes) return;
    graphData.nodes.forEach((n) => {
      n.fx = undefined;
      n.fy = undefined;
      if (is3D) n.fz = undefined;
    });
    setStopEngine(false);
    setGraphData({ ...graphData });
    setTimeout(() => {
      if (fgRef.current) fgRef.current.d3ReheatSimulation();
    }, 50);
  };

  const handleZoomToNode = (nodeId) => {
    const node = graphData?.nodes?.find((n) => n.id === nodeId);
    if (node && fgRef.current) {
      if (is3D) {
        fgRef.current.cameraPosition(
          { x: node.x, y: node.y, z: (node.z || 0) + 150 },
          { x: node.x, y: node.y, z: node.z || 0 },
          600,
        );
      } else {
        fgRef.current.centerAt(node.x, node.y, 600);
        fgRef.current.zoom(4, 600);
      }
      activeNodeRef.current = node;
      setActiveNode(node);
    }
  };

  const clearAllFilters = useCallback(() => {
    setBarSearch("");
    setSearchMatchedNodes(new Set());
    setSearchMatchedLinks(new Set());
    setActiveTypeFilter(new Set());
    setManualOnly(false);
    setHideOrphans(false);
    setDegreeHighlight(0);
  }, []);

  const switchToMyGraph = () => {
    setActiveGraphUuid(null);
    setGraphSourceLabel("My Graph");
    setStopEngine(false);
    needsZoomToFit.current = true;
    setBarSearch("");
    setSearchMatchedNodes(new Set());
    setSearchMatchedLinks(new Set());
    graphSourceMeta.current = { kind: "my_graph" };
    fetchBackendCounts();
    window.dispatchEvent(
      new CustomEvent("graph:active-source-changed", {
        detail: { kind: "my_graph" },
      }),
    );
    getGraphEdges(graphLimit).then((r) => {
      try {
        createGraph(r.data);
      } catch {}
    });
  };

  const switchToTeamGraph = (member) => {
    setActiveGraphUuid(member.uuid);
    setGraphSourceLabel(`${member.first_name} ${member.last_name}'s Graph`);
    setStopEngine(false);
    needsZoomToFit.current = true;
    setBarSearch("");
    setSearchMatchedNodes(new Set());
    setSearchMatchedLinks(new Set());
    graphSourceMeta.current = { kind: "team", uuid: member.uuid };
    fetchBackendCounts();
    window.dispatchEvent(
      new CustomEvent("graph:active-source-changed", {
        detail: { kind: "team", uuid: member.uuid },
      }),
    );
    getTeamMemberGraph(member.uuid, graphLimit).then((r) => {
      try {
        createGraph(r.data);
      } catch {}
    });
  };

  const switchToGlobalGraph = () => {
    setActiveGraphUuid(null);
    setGraphSourceLabel("Global");
    setStopEngine(false);
    needsZoomToFit.current = true;
    setBarSearch("");
    setSearchMatchedNodes(new Set());
    setSearchMatchedLinks(new Set());
    graphSourceMeta.current = { kind: "global" };
    fetchBackendCounts();
    window.dispatchEvent(
      new CustomEvent("graph:active-source-changed", {
        detail: { kind: "global" },
      }),
    );
    getGlobalGraph(graphLimit).then((r) => {
      try {
        createGraph(r.data);
      } catch {}
    });
  };

  const handleControlsSelect = (item) => {
    setStopEngine(false);
    needsZoomToFit.current = true;
    setBarSearch("");
    setSearchMatchedNodes(new Set());
    setSearchMatchedLinks(new Set());
    graphSourceMeta.current = { kind: item.type, value: item.value };
    fetchBackendCounts();
    window.dispatchEvent(
      new CustomEvent("graph:active-source-changed", {
        detail: { kind: item.type, value: item.value },
      }),
    );
    const fetchFn =
      item.type === "node"
        ? getConnectedNodeByType(item.value, graphLimit)
        : getConnectedEdgeByType(item.value, graphLimit);
    fetchFn
      .then((r) => {
        try {
          if (r.data?.status !== 501) {
            setGraphSourceLabel(item.value.replace(/_/g, " ").toLowerCase());
            createGraph(r.data);
          }
        } catch {}
      })
      .catch(() => {});
  };

  const applyCustomView = useCallback(
    (view, overrideLimit) => {
      const { node_types, edge_types, keywords, sources, target_types } =
        view.filters || {};
      setGraphSourceLabel(view.name);
      setStopEngine(false);
      needsZoomToFit.current = true;
      setBarSearch("");
      setSearchMatchedNodes(new Set());
      setSearchMatchedLinks(new Set());
      graphSourceMeta.current = { kind: "custom_view", view };
      fetchBackendCounts();
      window.dispatchEvent(
        new CustomEvent("graph:active-source-changed", {
          detail: { kind: "custom_view", viewId: view.document_id },
        }),
      );
      getFilteredGraphView({
        filters: {
          node_types,
          edge_types,
          keywords,
          sources,
          target_types,
          date_from: graphDateFrom,
          date_to: graphDateTo,
        },
        limit: overrideLimit ?? graphLimit,
      }).then((r) => {
        try {
          createGraph(r.data);
        } catch {}
      });
    },
    [graphLimit, graphDateFrom, graphDateTo],
  );

  const isFavorited = useCallback(
    (kind, value, teamUuid) =>
      graphFavorites.some(
        (f) =>
          f.favorite_kind === kind &&
          f.favorite_value === value &&
          (kind !== "team" || f.favorite_meta?.team_uuid === teamUuid),
      ),
    [graphFavorites],
  );

  const getFavoriteId = useCallback(
    (kind, value, teamUuid) => {
      const fav = graphFavorites.find(
        (f) =>
          f.favorite_kind === kind &&
          f.favorite_value === value &&
          (kind !== "team" || f.favorite_meta?.team_uuid === teamUuid),
      );
      return fav?.document_id || null;
    },
    [graphFavorites],
  );

  const toggleFavorite = useCallback(
    async (kind, value, label, meta = {}) => {
      const favId = getFavoriteId(kind, value, meta?.team_uuid);
      try {
        if (favId) await unfavoriteGraphView({ document_id: favId });
        else await favoriteGraphView({ kind, value, label, meta });
        loadGraphViewsData();
        window.dispatchEvent(new CustomEvent("sidebar:refresh-graph-views"));
      } catch (err) {
        console.error("Toggle favorite failed:", err);
      }
    },
    [getFavoriteId, loadGraphViewsData],
  );

  useEffect(() => {
    const handler = (e) => {
      const detail = e.detail;
      if (!detail) return;
      if (detail.kind === "my_graph") switchToMyGraph();
      else if (detail.kind === "global") switchToGlobalGraph();
      else if (detail.kind === "team") {
        const nameParts = (detail.meta?.team_name || detail.label || "").split(
          " ",
        );
        switchToTeamGraph({
          uuid: detail.meta?.team_uuid,
          first_name: nameParts[0] || "",
          last_name: nameParts.slice(1).join(" ") || "",
        });
      } else if (detail.kind === "node_type" || detail.kind === "node")
        handleControlsSelect({ type: "node", value: detail.value });
      else if (
        detail.kind === "edge_type" ||
        detail.kind === "edge" ||
        detail.kind === "link"
      )
        handleControlsSelect({ type: "link", value: detail.value });
      else if (detail.kind === "custom_view" && detail.view)
        applyCustomView(detail.view);
    };
    window.addEventListener("sidebar:graph-switch-source", handler);
    return () =>
      window.removeEventListener("sidebar:graph-switch-source", handler);
  }, [applyCustomView]);

  const refetchWithLimit = (newLimit) => {
    const meta = graphSourceMeta.current;
    setStopEngine(false);
    needsZoomToFit.current = true;
    if (meta.kind === "custom_view" && meta.view) {
      applyCustomView(meta.view, newLimit);
      return;
    }
    if (graphDateFrom || graphDateTo) {
      getFilteredGraphView({
        filters: {
          node_types: [],
          edge_types: [],
          keywords: [],
          sources: [],
          target_types: [],
          date_from: graphDateFrom,
          date_to: graphDateTo,
        },
        limit: newLimit,
      }).then((r) => {
        try {
          createGraph(r.data);
        } catch {}
      });
      return;
    }
    let fetchFn;
    if (meta.kind === "global") fetchFn = getGlobalGraph(newLimit);
    else if (meta.kind === "team")
      fetchFn = getTeamMemberGraph(meta.uuid, newLimit);
    else if (meta.kind === "node")
      fetchFn = getConnectedNodeByType(meta.value, newLimit);
    else if (meta.kind === "link")
      fetchFn = getConnectedEdgeByType(meta.value, newLimit);
    else fetchFn = getGraphEdges(newLimit);
    fetchFn.then((r) => {
      try {
        createGraph(r.data);
      } catch {}
    });
  };

  const executeSearch = useCallback(
    (query, onResults) => {
      if (!query || query.trim().length < 2) {
        setSearchMatchedNodes(new Set());
        setSearchMatchedLinks(new Set());
        if (query.trim().length === 0) refetchWithLimit(graphLimit);
        if (onResults) onResults([]);
        return;
      }
      const meta = graphSourceMeta.current;
      let scope = "";
      let scopeValue = "";
      let userUuid = "";
      if (meta.kind === "global") scope = "global";
      else if (meta.kind === "team") {
        scope = "team";
        userUuid = meta.uuid;
      } else if (meta.kind === "node") {
        scope = "node_type";
        scopeValue = meta.value;
      } else if (meta.kind === "link") {
        scope = "edge_type";
        scopeValue = meta.value;
      }
      searchGraph(query.trim(), graphLimit, scope, userUuid, scopeValue)
        .then((r) => {
          try {
            const data = r.data;
            if (data?.status === 200) {
              setStopEngine(false);
              needsZoomToFit.current = true;
              createGraph(data);
              setSearchMatchedNodes(new Set(data.matched_nodes || []));
              setSearchMatchedLinks(new Set(data.matched_links || []));
              const matchedSet = new Set(data.matched_nodes || []);
              const preview = (data.nodes || [])
                .filter((n) => matchedSet.has(n.name))
                .slice(0, 12);
              if (onResults) onResults(preview);
            } else {
              if (onResults) onResults([]);
            }
          } catch {
            if (onResults) onResults([]);
          }
        })
        .catch(() => {
          if (onResults) onResults([]);
        });
    },
    [graphLimit],
  );

  const handleBarSearchChange = useCallback((value) => setBarSearch(value), []);
  const handleClearSearch = useCallback(() => {
    setBarSearch("");
    setSearchMatchedNodes(new Set());
    setSearchMatchedLinks(new Set());
    refetchWithLimit(graphLimit);
  }, [graphLimit]);

  useEffect(() => {
    refetchWithLimit(graphLimit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graphDateFrom, graphDateTo]);

  const handleUndo = useCallback(
    (toast) => {
      const skipApi = showTutorial;
      if (toast.type === "connect-existing") {
        setGraphData((prev) => ({
          nodes: prev.nodes.map((n) => {
            if (n.x != null && n.fx == null) {
              n.fx = n.x;
              n.fy = n.y;
              if (is3D) n.fz = n.z;
            }
            return n;
          }),
          links: prev.links.filter(
            (l) =>
              !(
                (l.source === toast.undoPayload.source ||
                  l.source?.id === toast.undoPayload.source) &&
                (l.target === toast.undoPayload.target ||
                  l.target?.id === toast.undoPayload.target)
              ),
          ),
        }));
        if (!skipApi)
          deleteEdge({
            source: toast.undoPayload.source,
            target: toast.undoPayload.target,
          }).catch(console.error);
      } else if (toast.type === "create-and-connect") {
        const nodeName = toast.undoPayload.newNode.name;
        setGraphData((prev) => ({
          nodes: prev.nodes
            .filter((n) => n.name !== nodeName)
            .map((n) => {
              if (n.x != null && n.fx == null) {
                n.fx = n.x;
                n.fy = n.y;
                if (is3D) n.fz = n.z;
              }
              return n;
            }),
          links: prev.links.filter((l) => {
            const s = typeof l.source === "object" ? l.source.name : l.source;
            const t = typeof l.target === "object" ? l.target.name : l.target;
            return !(s === nodeName || t === nodeName);
          }),
        }));
        if (!skipApi) deleteNode({ name: nodeName }).catch(console.error);
      } else if (toast.type === "create-standalone") {
        const nodeName = toast.undoPayload.newNode.name;
        setGraphData((prev) => ({
          nodes: prev.nodes
            .filter((n) => n.name !== nodeName)
            .map((n) => {
              if (n.x != null && n.fx == null) {
                n.fx = n.x;
                n.fy = n.y;
                if (is3D) n.fz = n.z;
              }
              return n;
            }),
          links: prev.links,
        }));
        if (!skipApi) deleteNode({ name: nodeName }).catch(console.error);
      } else if (toast.type === "delete-node") {
        const { node: savedNode, edges: savedEdges } = toast.undoPayload;
        setGraphData((prev) => ({
          nodes: [
            ...prev.nodes.map((n) => {
              if (n.x != null && n.fx == null) {
                n.fx = n.x;
                n.fy = n.y;
                if (is3D) n.fz = n.z;
              }
              return n;
            }),
            savedNode,
          ],
          links: [...prev.links, ...savedEdges],
        }));
        if (!skipApi) {
          createNode({
            name: savedNode.name,
            type: savedNode.icon || "PERSON",
            context: savedNode.context || "restored",
            isPrivate: savedNode.isPrivate !== false,
          })
            .then((res) => {
              const newDocId = res.data.node.document_id;
              setGraphData((prev) => ({
                ...prev,
                nodes: prev.nodes.map((n) =>
                  n.name === savedNode.name && !n.document_id
                    ? { ...n, document_id: newDocId }
                    : n,
                ),
              }));
              savedEdges.forEach((e) => {
                const src =
                  typeof e.source === "object" ? e.source.id : e.source;
                const tgt =
                  typeof e.target === "object" ? e.target.id : e.target;
                const otherName = src === savedNode.name ? tgt : src;
                setGraphData((prev) => {
                  const otherNode = prev.nodes.find((n) => n.id === otherName);
                  if (otherNode?.document_id) {
                    createEdge({
                      source:
                        src === savedNode.name
                          ? newDocId
                          : otherNode.document_id,
                      target:
                        tgt === savedNode.name
                          ? newDocId
                          : otherNode.document_id,
                      source_name: src,
                      source_type:
                        src === savedNode.name
                          ? savedNode.icon || "PERSON"
                          : otherNode.icon || "PERSON",
                      target_name: tgt,
                      target_type:
                        tgt === savedNode.name
                          ? savedNode.icon || "PERSON"
                          : otherNode.icon || "PERSON",
                      relationship: e.label || "CONNECTED_TO",
                      context: e.context || "restored",
                      isPrivate: e.isPrivate !== false,
                    }).catch(console.error);
                  }
                  return prev;
                });
              });
            })
            .catch(console.error);
        }
      } else if (toast.type === "delete-edge") {
        const { edge: savedEdge } = toast.undoPayload;
        setGraphData((prev) => {
          const src =
            typeof savedEdge.source === "object"
              ? savedEdge.source.id
              : savedEdge.source;
          const tgt =
            typeof savedEdge.target === "object"
              ? savedEdge.target.id
              : savedEdge.target;
          const srcNode = prev.nodes.find((n) => n.id === src);
          const tgtNode = prev.nodes.find((n) => n.id === tgt);
          if (srcNode?.document_id && tgtNode?.document_id && !skipApi) {
            createEdge({
              source: srcNode.document_id,
              target: tgtNode.document_id,
              source_name: src,
              source_type: srcNode.icon || "PERSON",
              target_name: tgt,
              target_type: tgtNode.icon || "PERSON",
              relationship: savedEdge.label || "CONNECTED_TO",
              context: savedEdge.context || "restored",
              isPrivate: savedEdge.isPrivate !== false,
            }).catch(console.error);
          }
          return { ...prev, links: [...prev.links, savedEdge] };
        });
      }
      dismissToast(toast.id);
    },
    [dismissToast, showTutorial, is3D],
  );

  useEffect(() => {
    if (!isRightOpen) {
      activeNodeRef.current = null;
      setActiveNode(null);
    }
  }, [isRightOpen]);
  useEffect(() => {
    return () => Object.values(toastTimersRef.current).forEach(clearTimeout);
  }, []);

  const measure = () => {
    if (!divRef.current) return;
    const rect = divRef.current.getBoundingClientRect();
    setDimensions({
      width: Math.max(0, Math.floor(rect.width)),
      height: Math.max(0, Math.floor(rect.height)),
    });
  };

  useLayoutEffect(() => {
    measure();
    if (!divRef.current) return;
    const ro = new ResizeObserver(() => measure());
    ro.observe(divRef.current);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    measure();
    const t1 = setTimeout(measure, 120),
      t2 = setTimeout(measure, 260),
      t3 = setTimeout(measure, 500);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [isLeftOpen, isRightOpen, rightWidth, LEFT_WIDTH]);

  useEffect(() => {
    if (!fgRef.current) return;
    fgRef.current.d3ReheatSimulation?.();
    if (!isLeftOpen && !isRightOpen)
      requestAnimationFrame(() => fgRef.current?.d3ReheatSimulation?.());
  }, [dimensions.width, dimensions.height, isLeftOpen, isRightOpen]);

  useEffect(() => {
    const handleResize = () => {
      if (divRef.current)
        setDimensions({
          width: divRef.current.offsetWidth,
          height: divRef.current.offsetHeight,
        });
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  function createGraph(data) {
    try {
      const d = typeof data === "string" ? JSON.parse(data) : data;
      if (d?.nodes.length === 0 && d?.links.length === 0) {
        setIsGraphEmpty(true);
        setIsLoading(false);
        return;
      }
      setIsGraphEmpty(false);
      const nodes_arr = d.nodes.map((item) => ({
        id: item.name,
        name: item.name,
        group: item.group,
        icon: item.icon,
        img: safeIcon(item.icon),
        document_id: item.document_id,
      }));
      const temp = nodes_arr.reduce((acc, item) => {
        if (!acc[item.name]) acc[item.name] = item;
        return acc;
      }, {});
      d.links.forEach((l) => {
        if (!temp[l.source])
          temp[l.source] = {
            id: l.source,
            name: l.source,
            group: "orphan",
            icon: "PERSON",
            img: safeIcon("PERSON"),
            document_id: l.document_id || null,
          };
        if (!temp[l.target])
          temp[l.target] = {
            id: l.target,
            name: l.target,
            group: "orphan",
            icon: "PERSON",
            img: safeIcon("PERSON"),
            document_id: l.document_id || null,
          };
      });
      setGraphData({ nodes: Object.values(temp), links: d.links });
      setIsLoading(false);
      setTimeout(() => {
        if (fgRef.current) fgRef.current.zoomToFit?.(400, 60);
      }, 300);
    } catch (e) {
      console.log(e);
    }
  }

  useEffect(() => {
    setConnectedTypes([]);
    setFilteredResults([]);
    setSearchTitle(null);
    setIsGraphEmpty(false);
    setIsLoading(true);
    getAllConnectedNodeEdgesTypes().then((response) => {
      try {
        const linkResults = response.data.link_types.map((item) => ({
          type: "link",
          value: item,
        }));
        const nodeResults = response.data.nodes_types.map((item) => ({
          type: "node",
          value: item,
        }));
        setConnectedTypes((prev) => {
          const combined = [...prev, ...linkResults, ...nodeResults];
          const unique = Array.from(
            new Map(combined.map((i) => [i.value, i])).values(),
          );
          return unique.sort((a, b) => a.value.localeCompare(b.value));
        });
      } catch (e) {
        console.log(e);
      }
    });
    getGraphEdges(graphLimit).then((response) => {
      try {
        createGraph(response.data);
      } catch (e) {
        console.log(e);
      }
    });
    fetchBackendCounts();
  }, []);

  useEffect(() => {
    setFilteredResults(
      searchEntry !== ""
        ? connectedTypes.filter((i) =>
            i.value.toLowerCase().includes(searchEntry.toLowerCase()),
          )
        : connectedTypes,
    );
  }, [searchEntry, connectedTypes]);

  const linkTypes = connectedTypes
    .filter((ct) => ct.type === "link")
    .map((ct) => ct.value);

  const handleCreateNewNode = () => {
    if (!newNodeForm.name.trim() || !newNodeSourceNode) return;
    const icon = safeIcon(newNodeForm.type);
    const tempId = `__temp_${Date.now()}`;
    const newNode = {
      id: tempId,
      name: newNodeForm.name,
      group: "manual",
      icon: newNodeForm.type,
      img: icon,
      document_id: null,
      context: newNodeForm.context,
      isPrivate: newNodeForm.isPrivate,
    };
    const canvasRect = divRef.current
      ?.querySelector("canvas")
      ?.getBoundingClientRect();
    if (canvasRect && fgRef.current) {
      if (is3D) {
        const srcNode = newNodeSourceNode;
        const offset = 50 + Math.random() * 30;
        const angle = Math.random() * Math.PI * 2;
        const phi = Math.random() * Math.PI;
        newNode.x = (srcNode.x || 0) + Math.sin(phi) * Math.cos(angle) * offset;
        newNode.y = (srcNode.y || 0) + Math.sin(phi) * Math.sin(angle) * offset;
        newNode.z = (srcNode.z || 0) + Math.cos(phi) * offset;
        newNode.fx = newNode.x;
        newNode.fy = newNode.y;
        newNode.fz = newNode.z;
      } else {
        const gPos = fgRef.current.screen2GraphCoords(
          newNodeModalPos.x - canvasRect.left,
          newNodeModalPos.y - canvasRect.top,
        );
        newNode.x = gPos.x;
        newNode.y = gPos.y;
        newNode.fx = gPos.x;
        newNode.fy = gPos.y;
      }
    }
    const sourceId = newNodeSourceNode.id;
    const sourceName = newNodeSourceNode.name;
    const sourceDocId = newNodeSourceNode.document_id;
    setGraphData((prev) => ({
      nodes: [
        ...prev.nodes.map((n) => {
          if (n.x != null && n.fx == null) {
            n.fx = n.x;
            n.fy = n.y;
            if (is3D) n.fz = n.z;
          }
          return n;
        }),
        newNode,
      ],
      links: [
        ...prev.links,
        {
          source: sourceId,
          target: tempId,
          label: newNodeForm.relationship || "CONNECTED_TO",
          manual: true,
        },
      ],
    }));
    pushToast(
      `Created "${newNodeForm.name}" and connected from "${sourceName}"`,
      {
        newNode: { id: tempId, name: newNode.name, type: newNodeForm.type },
        edge: { source: sourceId, target: tempId },
      },
      "create-and-connect",
    );
    setShowNewNodeModal(false);
    setNewNodeSourceNode(null);
    if (showTutorial) return;
    createNode({
      name: newNodeForm.name,
      type: newNodeForm.type,
      context: newNodeForm.context,
      isPrivate: newNodeForm.isPrivate,
    })
      .then((res) => {
        const created = res.data.node;
        setGraphData((prev) => ({
          nodes: prev.nodes.map((n) =>
            n.id === tempId
              ? { ...n, id: created.id, document_id: created.document_id }
              : n,
          ),
          links: prev.links.map((l) => ({
            ...l,
            source:
              l.source === tempId || l.source?.id === tempId
                ? created.id
                : l.source,
            target:
              l.target === tempId || l.target?.id === tempId
                ? created.id
                : l.target,
          })),
        }));
        return createEdge({
          source: sourceDocId,
          target: created.document_id,
          source_name: sourceName,
          source_type: newNodeSourceNode.icon || "PERSON",
          target_name: newNodeForm.name,
          target_type: newNodeForm.type,
          relationship: newNodeForm.relationship || "CONNECTED_TO",
          context: newNodeForm.edgeContext,
          isPrivate: newNodeForm.isPrivate,
        });
      })
      .then((edgeRes) => {
        if (edgeRes?.data?.edge?.document_id) {
          const {
            document_id: edgeDocId,
            source: edgeSrc,
            target: edgeTgt,
          } = edgeRes.data.edge;
          setGraphData((prev) => ({
            ...prev,
            links: prev.links.map((l) => {
              const lSrc =
                typeof l.source === "object" ? l.source.id : l.source;
              const lTgt =
                typeof l.target === "object" ? l.target.id : l.target;
              return lSrc === edgeSrc && lTgt === edgeTgt && !l.document_id
                ? { ...l, document_id: edgeDocId }
                : l;
            }),
          }));
        }
      })
      .catch((err) => {
        console.error("Create node + edge failed:", err);
        setGraphData((prev) => ({
          nodes: prev.nodes.filter((n) => n.id !== tempId),
          links: prev.links.filter(
            (l) =>
              !(
                l.source === tempId ||
                l.source?.id === tempId ||
                l.target === tempId ||
                l.target?.id === tempId
              ),
          ),
        }));
      });
  };

  const handleCreateStandaloneNode = () => {
    if (!standaloneForm.name.trim()) return;
    const icon = safeIcon(standaloneForm.type);
    const tempId = `__temp_${Date.now()}`;
    const newNode = {
      id: tempId,
      name: standaloneForm.name,
      group: "manual",
      icon: standaloneForm.type,
      img: icon,
      document_id: null,
      context: standaloneForm.context,
      isPrivate: standaloneForm.isPrivate,
    };
    if (standaloneGraphPosRef.current) {
      newNode.x = standaloneGraphPosRef.current.x;
      newNode.y = standaloneGraphPosRef.current.y;
      if (is3D) newNode.z = standaloneGraphPosRef.current.z || 0;
      newNode.fx = newNode.x;
      newNode.fy = newNode.y;
      if (is3D) newNode.fz = newNode.z;
    } else {
      newNode.x = (Math.random() - 0.5) * 40;
      newNode.y = (Math.random() - 0.5) * 40;
      if (is3D) newNode.z = (Math.random() - 0.5) * 40;
      newNode.fx = newNode.x;
      newNode.fy = newNode.y;
      if (is3D) newNode.fz = newNode.z;
    }
    setGraphData((prev) => ({
      nodes: [
        ...prev.nodes.map((n) => {
          if (n.x != null && n.fx == null) {
            n.fx = n.x;
            n.fy = n.y;
            if (is3D) n.fz = n.z;
          }
          return n;
        }),
        newNode,
      ],
      links: prev.links,
    }));
    pushToast(
      `Created standalone node "${standaloneForm.name}"`,
      {
        newNode: { id: tempId, name: newNode.name, type: standaloneForm.type },
      },
      "create-standalone",
    );
    setShowStandaloneModal(false);
    setStandaloneForm({
      name: "",
      type: "PERSON",
      context: "",
      isPrivate: false,
    });
    if (showTutorial) return;
    createNode({
      name: standaloneForm.name,
      type: standaloneForm.type,
      context: standaloneForm.context,
      isPrivate: standaloneForm.isPrivate,
    })
      .then((res) => {
        const created = res.data.node;
        setGraphData((prev) => ({
          ...prev,
          nodes: prev.nodes.map((n) =>
            n.id === tempId
              ? { ...n, id: created.id, document_id: created.document_id }
              : n,
          ),
        }));
      })
      .catch((err) => {
        console.error("Create standalone node failed:", err);
        setGraphData((prev) => ({
          ...prev,
          nodes: prev.nodes.filter((n) => n.id !== tempId),
        }));
      });
  };

  const handleConfirmConnect = () => {
    if (!pendingConnect) return;
    const { source, target, relationship } = pendingConnect;
    setGraphData((prev) => ({
      nodes: prev.nodes.map((n) => {
        if (n.x != null && n.fx == null) {
          n.fx = n.x;
          n.fy = n.y;
          if (is3D) n.fz = n.z;
        }
        return n;
      }),
      links: [
        ...prev.links,
        {
          source: source.id,
          target: target.id,
          label: relationship,
          manual: true,
          context: pendingConnect.context,
          isPrivate: pendingConnect.isPrivate,
        },
      ],
    }));
    pushToast(
      `Connected "${source.name}" → "${target.name}"`,
      { source: source.id, target: target.id },
      "connect-existing",
    );
    if (!showTutorial) {
      createEdge({
        source: source.document_id,
        target: target.document_id,
        source_name: source.name,
        source_type: source.icon || "PERSON",
        target_name: target.name,
        target_type: target.icon || "PERSON",
        relationship,
        context: pendingConnect.context,
        isPrivate: pendingConnect.isPrivate,
      })
        .then((res) => {
          const edgeDocId = res.data.edge?.document_id;
          if (edgeDocId)
            setGraphData((prev) => ({
              ...prev,
              links: prev.links.map((l) => {
                const lSrc =
                  typeof l.source === "object" ? l.source.id : l.source;
                const lTgt =
                  typeof l.target === "object" ? l.target.id : l.target;
                return lSrc === source.id &&
                  lTgt === target.id &&
                  !l.document_id
                  ? { ...l, document_id: edgeDocId }
                  : l;
              }),
            }));
        })
        .catch((err) => {
          console.error("Create edge failed:", err);
          setGraphData((prev) => ({
            ...prev,
            links: prev.links.filter(
              (l) =>
                !(
                  (l.source === source.id || l.source?.id === source.id) &&
                  (l.target === target.id || l.target?.id === target.id)
                ),
            ),
          }));
        });
    }
    setPendingConnect(null);
  };

  const openEditModal = (item, kind, sx, sy) => {
    setContextMenu(null);
    if (kind === "node") {
      setEditModal({
        item,
        kind,
        form: {
          name: item.name,
          type: item.icon || item.group || "PERSON",
          context: item.context || "",
          isPrivate: item.isPrivate !== false,
        },
        screenX: sx,
        screenY: sy,
      });
    } else {
      const src =
        typeof item.source === "object" ? item.source.id : item.source;
      const tgt =
        typeof item.target === "object" ? item.target.id : item.target;
      setEditModal({
        item,
        kind,
        form: {
          source: src,
          target: tgt,
          relationship: item.label || "CONNECTED_TO",
          context: item.context || "",
          isPrivate: item.isPrivate !== false,
        },
        screenX: sx,
        screenY: sy,
      });
    }
  };

  const openDeleteModal = (item, kind, sx, sy) => {
    setContextMenu(null);
    setDeleteModal({ item, kind, screenX: sx, screenY: sy });
  };

  const handleEditConfirm = () => {
    if (!editModal) return;
    const { item, kind, form } = editModal;
    if (kind === "node") {
      const oldName = item.name;
      const newName = form.name.trim();
      const newType = form.type;
      if (!newName) return;
      setGraphData((prev) => ({
        nodes: prev.nodes.map((n) =>
          n.id === item.id
            ? {
                ...n,
                id: newName,
                name: newName,
                icon: newType,
                group: "manual",
                context: form.context,
                isPrivate: form.isPrivate,
              }
            : n,
        ),
        links: prev.links.map((l) => ({
          ...l,
          source:
            l.source === oldName || l.source?.id === oldName
              ? newName
              : l.source,
          target:
            l.target === oldName || l.target?.id === oldName
              ? newName
              : l.target,
        })),
      }));
      if (!showTutorial)
        updateNode({
          old_name: oldName,
          name: newName,
          type: newType,
          context: form.context,
          isPrivate: form.isPrivate,
        }).catch(console.error);
    } else {
      const src =
        typeof item.source === "object" ? item.source.id : item.source;
      const tgt =
        typeof item.target === "object" ? item.target.id : item.target;
      setGraphData((prev) => ({
        ...prev,
        links: prev.links.map((l) => {
          const lSrc = typeof l.source === "object" ? l.source.id : l.source;
          const lTgt = typeof l.target === "object" ? l.target.id : l.target;
          return lSrc === src && lTgt === tgt && l.manual
            ? {
                ...l,
                label: form.relationship,
                context: form.context,
                isPrivate: form.isPrivate,
              }
            : l;
        }),
      }));
      if (!showTutorial)
        updateEdge({
          source: src,
          target: tgt,
          relationship: form.relationship,
          context: form.context,
          isPrivate: form.isPrivate,
        }).catch(console.error);
    }
    setEditModal(null);
  };

  const handleDeleteConfirm = () => {
    if (!deleteModal) return;
    const { item, kind } = deleteModal;
    if (kind === "node") {
      const nodeName = item.name;
      const savedNode = graphData.nodes.find((n) => n.id === item.id);
      const savedEdges = graphData.links.filter((l) => {
        const src = typeof l.source === "object" ? l.source.id : l.source;
        const tgt = typeof l.target === "object" ? l.target.id : l.target;
        return l.manual && (src === nodeName || tgt === nodeName);
      });
      setGraphData((prev) => ({
        nodes: prev.nodes.filter((n) => n.id !== item.id),
        links: prev.links.filter((l) => {
          const src = typeof l.source === "object" ? l.source.id : l.source;
          const tgt = typeof l.target === "object" ? l.target.id : l.target;
          return !(l.manual && (src === nodeName || tgt === nodeName));
        }),
      }));
      pushToast(
        `Deleted node "${nodeName}"`,
        { node: savedNode, edges: savedEdges },
        "delete-node",
      );
      if (!showTutorial) deleteNode({ name: nodeName }).catch(console.error);
    } else {
      const src =
        typeof item.source === "object" ? item.source.id : item.source;
      const tgt =
        typeof item.target === "object" ? item.target.id : item.target;
      const savedEdge = graphData.links.find((l) => {
        const lSrc = typeof l.source === "object" ? l.source.id : l.source;
        const lTgt = typeof l.target === "object" ? l.target.id : l.target;
        return lSrc === src && lTgt === tgt && l.manual;
      });
      setGraphData((prev) => ({
        ...prev,
        links: prev.links.filter((l) => {
          const lSrc = typeof l.source === "object" ? l.source.id : l.source;
          const lTgt = typeof l.target === "object" ? l.target.id : l.target;
          return !(lSrc === src && lTgt === tgt && l.manual);
        }),
      }));
      pushToast(
        `Deleted edge "${src}" → "${tgt}"`,
        { edge: savedEdge },
        "delete-edge",
      );
      if (!showTutorial)
        deleteEdge({ source: src, target: tgt }).catch(console.error);
    }
    setDeleteModal(null);
  };

  const getDragLineCoords = () => {
    if (!connectDrag?.sourceNode || !fgRef.current || !divRef.current)
      return null;
    const canvasRect = divRef.current
      ?.querySelector("canvas")
      ?.getBoundingClientRect();
    const wrapperRect = divRef.current.getBoundingClientRect();
    if (!canvasRect) return null;
    const { sourceNode, currentScreenPos } = connectDrag;
    const ss = fgRef.current.graph2ScreenCoords(
      sourceNode.x,
      sourceNode.y,
      sourceNode.z || 0,
    );
    const cx = ss.x + (canvasRect.left - wrapperRect.left);
    const cy = ss.y + (canvasRect.top - wrapperRect.top);
    const ex = currentScreenPos.x - wrapperRect.left;
    const ey = currentScreenPos.y - wrapperRect.top;
    const dx = ex - cx,
      dy = ey - cy;
    const dist = Math.sqrt(dx * dx + dy * dy) || 1;
    let edgeOffset;
    if (is3D && fgRef.current?.camera) {
      const p = fgRef.current.camera().position;
      const cdx = p.x - (sourceNode.x || 0),
        cdy = p.y - (sourceNode.y || 0),
        cdz = p.z - (sourceNode.z || 0);
      edgeOffset = Math.max(
        8,
        (CIRCLE_RADIUS * 0.72 * 800) /
          Math.sqrt(cdx * cdx + cdy * cdy + cdz * cdz),
      );
    } else {
      edgeOffset = CIRCLE_RADIUS * zoomRef.current + 4;
    }
    const x1 = cx + (dx / dist) * edgeOffset,
      y1 = cy + (dy / dist) * edgeOffset;
    let x2 = ex,
      y2 = ey;
    if (connectTargetNode) {
      const ts = fgRef.current.graph2ScreenCoords(
        connectTargetNode.x,
        connectTargetNode.y,
        connectTargetNode.z || 0,
      );
      const tx = ts.x + (canvasRect.left - wrapperRect.left);
      const ty = ts.y + (canvasRect.top - wrapperRect.top);
      const tdx = tx - x1,
        tdy = ty - y1;
      const tdist = Math.sqrt(tdx * tdx + tdy * tdy) || 1;
      x2 = tx - (tdx / tdist) * edgeOffset;
      y2 = ty - (tdy / tdist) * edgeOffset;
    }
    return { x1, y1, x2, y2 };
  };
  const dragLine = getDragLineCoords();

  const truncate = (str, max = 40) =>
    !str ? "" : str.length > max ? str.slice(0, max) + "…" : str;
  const formatType = (iconName) =>
    !iconName
      ? ""
      : iconName.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  const rendererProps = {
    fgRef,
    divRef,
    hoveredNodeRef,
    selectedNodeIdsRef,
    connectDragRef,
    selectionBoxRef,
    lastDragPosRef,
    suppressClickRef,
    cursorGraphPosRef,
    tooltipRafRef,
    zoomRef,
    needsZoomToFit,
    standaloneGraphPosRef,
    standaloneScreenPosRef,
    iconImgCacheRef,
    graphData,
    setGraphData,
    isDraggingFromPlus,
    setIsDraggingFromPlus,
    stopEngine,
    setStopEngine,
    dimensions,
    isLoading,
    isGraphEmpty,
    setTooltipNode,
    setConnectDrag,
    setSelectionBox,
    setConnectTargetNode,
    setNodeMenu,
    setContextMenu,
    setActiveNode: (n) => {
      activeNodeRef.current = n;
      setActiveNode(n);
    },
    setPendingConnect,
    setShowNewNodeModal,
    setNewNodeModalPos,
    setNewNodeForm,
    setNewNodeSourceNode,
    setShowStandaloneModal,
    setStandaloneForm,
    setSelectedNodeIds,
    hasActiveFilter,
    visibleNodeIds,
    degreeMap,
    degreeHighlight,
    searchMatchedNodes,
    searchMatchedLinks,
    nodeMenu,
    connectTargetNode,
    t,
    dark,
    showEdgeLabels,
    warmupTicks,
    linkTypes,
    handleFgNodeClick,
    handleFgLinkClick,
    handleFgNodeHover,
    linkColorFn,
    linkWidthFn,
    openEditModal,
    openDeleteModal,
    activeNodeRef,
  };

  return (
    <div
      ref={divRef}
      className="relative flex flex-col w-full h-full overflow-hidden rounded-md"
    >
      {/* ── Toasts ── */}
      {toasts.length > 0 && (
        <div className="absolute top-20 left-1/2 -translate-x-1/2 z-[60] flex flex-col gap-2 pointer-events-none">
          {toasts.map((toast) => (
            <div
              key={toast.id}
              className="pointer-events-auto flex items-center gap-3 px-4 py-2.5 rounded-lg backdrop-blur-md shadow-lg animate-[slideDown_0.3s_ease-out] relative"
              style={{
                background: t.surface.bg,
                border: `1px solid ${t.surface.border}`,
                boxShadow: t.surface.toastShadow,
              }}
            >
              <div
                className={`flex-shrink-0 w-5 h-5 rounded-full ${t.check.bg} border ${t.check.border} flex items-center justify-center`}
              >
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 10 10"
                  fill="none"
                  className={t.check.icon}
                >
                  <path
                    d="M2 5.5L4 7.5L8 3"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <span
                className={`${t.text.primary} text-xs font-light whitespace-nowrap max-w-[320px] truncate`}
              >
                {toast.message}
              </span>
              <button
                className="flex-shrink-0 ml-1 px-2.5 py-1 rounded text-[11px] font-medium text-sky-500 hover:text-white bg-sky-500/10 hover:bg-sky-500/30 border border-sky-500/20 hover:border-sky-500/40 transition-all duration-150"
                onClick={() => handleUndo(toast)}
              >
                Undo
              </button>
              <button
                className={`flex-shrink-0 ${t.btn.dismissIcon} transition-colors`}
                onClick={() => dismissToast(toast.id)}
              >
                {React.createElement(X, { size: 13 })}
              </button>
              <div
                className={`absolute bottom-0 left-0 h-[2px] ${t.progressBar} rounded-b-lg`}
                style={{
                  animation: "shrinkWidth 10s linear forwards",
                  width: "100%",
                }}
              />
            </div>
          ))}
        </div>
      )}

      <style>{`
        @keyframes slideDown { from { opacity: 0; transform: translateY(-12px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes shrinkWidth { from { width: 100%; } to { width: 0%; } }
        @keyframes spin { from { transform: translateY(-50%) rotate(0deg); } to { transform: translateY(-50%) rotate(360deg); } }
        .type-pills-scroll::-webkit-scrollbar { display: none; }
        .type-pills-scroll { -ms-overflow-style: none; scrollbar-width: none; }
        input[type="range"] { -webkit-appearance: none; appearance: none; height: 4px; border-radius: 2px; background: ${dark ? "rgba(14,165,233,0.15)" : "rgba(100,116,139,0.2)"}; }
        input[type="range"]::-webkit-slider-thumb { -webkit-appearance: none; appearance: none; width: 12px; height: 12px; border-radius: 50%; background: rgba(14,165,233,0.8); cursor: pointer; }
        ${PANEL_KEYFRAMES}
      `}</style>

      {/* ── Floating modals ── */}
      {showStandaloneModal && (
        <FloatingPanel
          t={t}
          dark={dark}
          title="Add New Node"
          initialX={standaloneScreenPosRef.current.x}
          initialY={standaloneScreenPosRef.current.y}
        >
          <div
            className={`mb-3 px-3 py-2 rounded ${t.badge.bg} border ${t.badge.border}`}
          >
            <span className={`${t.text.secondary} text-xs`}>
              This node will be added without any connections.
            </span>
          </div>
          <ModalInput
            t={t}
            label="Node Name"
            placeholder="Enter node name..."
            value={standaloneForm.name}
            onChange={(e) =>
              setStandaloneForm((f) => ({ ...f, name: e.target.value }))
            }
            onKeyDown={(e) => {
              if (e.key === "Enter") handleCreateStandaloneNode();
              if (e.key === "Escape") setShowStandaloneModal(false);
            }}
          />
          <ModalDropdown
            t={t}
            label="Node Type"
            value={standaloneForm.type}
            options={NODE_TYPE_OPTIONS}
            onChange={(e) =>
              setStandaloneForm((f) => ({ ...f, type: e.target.value }))
            }
          />
          <ModalTextarea
            t={t}
            label="Context"
            placeholder="Add description or notes..."
            value={standaloneForm.context}
            onChange={(e) =>
              setStandaloneForm((f) => ({ ...f, context: e.target.value }))
            }
          />
          <ModalToggle
            t={t}
            label="Private"
            checked={standaloneForm.isPrivate}
            onChange={(v) => setStandaloneForm((f) => ({ ...f, isPrivate: v }))}
          />
          <ModalActions
            t={t}
            onCancel={() => setShowStandaloneModal(false)}
            onConfirm={handleCreateStandaloneNode}
            confirmLabel="Add Node"
            disabled={
              !standaloneForm.name.trim() || !standaloneForm.context.trim()
            }
          />
        </FloatingPanel>
      )}

      {pendingConnect && (
        <FloatingPanel
          t={t}
          dark={dark}
          title="Connect Nodes"
          initialX={pendingConnect.screenX}
          initialY={pendingConnect.screenY}
        >
          <div
            className={`mb-3 px-3 py-2 rounded ${t.badge.bg} border ${t.badge.border}`}
          >
            <span className={`${t.text.accent} text-xs font-medium`}>
              {pendingConnect.source.name}
            </span>
            <span className={`${t.text.secondary} text-xs`}> → </span>
            <span className={`${t.text.accent} text-xs font-medium`}>
              {pendingConnect.target.name}
            </span>
          </div>
          <ModalDropdown
            t={t}
            label="Relationship"
            value={pendingConnect.relationship}
            options={linkTypes}
            onChange={(e) =>
              setPendingConnect((prev) => ({
                ...prev,
                relationship: e.target.value,
              }))
            }
          />
          <ModalTextarea
            t={t}
            label="Context"
            placeholder="Add description or notes..."
            value={pendingConnect.context}
            onChange={(e) =>
              setPendingConnect((prev) => ({
                ...prev,
                context: e.target.value,
              }))
            }
          />
          <ModalToggle
            t={t}
            label="Private"
            checked={pendingConnect.isPrivate}
            onChange={(v) =>
              setPendingConnect((prev) => ({ ...prev, isPrivate: v }))
            }
          />
          <ModalActions
            t={t}
            onCancel={() => setPendingConnect(null)}
            onConfirm={handleConfirmConnect}
            confirmLabel="Connect"
            disabled={
              !pendingConnect.relationship || !pendingConnect.context?.trim()
            }
          />
        </FloatingPanel>
      )}

      {contextMenu && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setContextMenu(null)}
          />
          <div
            className="fixed z-50 rounded-lg shadow-lg backdrop-blur-md py-1 min-w-[140px]"
            style={{
              left: contextMenu.x,
              top: contextMenu.y,
              background: t.surface.popupBg,
              border: `1px solid ${t.surface.border}`,
              boxShadow: t.surface.popupShadow,
            }}
          >
            <button
              className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 transition-colors ${t.text.primary}`}
              style={{ cursor: "pointer" }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = t.surface.activeRow;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
              }}
              onClick={() =>
                openEditModal(
                  contextMenu.item,
                  contextMenu.kind,
                  contextMenu.x,
                  contextMenu.y,
                )
              }
            >
              <i
                className="fal fa-pencil"
                style={{ fontSize: 11, opacity: 0.7 }}
              />
              Edit {contextMenu.kind === "node" ? "Node" : "Edge"}
            </button>
            <button
              className="w-full text-left px-3 py-2 text-xs flex items-center gap-2 transition-colors"
              style={{ cursor: "pointer", color: "rgba(239,68,68,0.9)" }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = t.surface.activeRow;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
              }}
              onClick={() =>
                openDeleteModal(
                  contextMenu.item,
                  contextMenu.kind,
                  contextMenu.x,
                  contextMenu.y,
                )
              }
            >
              <i className="fal fa-trash" style={{ fontSize: 11 }} />
              Delete {contextMenu.kind === "node" ? "Node" : "Edge"}
            </button>
          </div>
        </>
      )}

      {editModal && (
        <FloatingPanel
          t={t}
          dark={dark}
          title={editModal.kind === "node" ? "Edit Node" : "Edit Edge"}
          initialX={editModal.screenX}
          initialY={editModal.screenY}
        >
          {editModal.kind === "node" ? (
            <>
              <ModalInput
                t={t}
                label="Node Name"
                placeholder="Enter node name..."
                value={editModal.form.name}
                onChange={(e) =>
                  setEditModal((prev) => ({
                    ...prev,
                    form: { ...prev.form, name: e.target.value },
                  }))
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleEditConfirm();
                  if (e.key === "Escape") setEditModal(null);
                }}
              />
              <ModalDropdown
                t={t}
                label="Node Type"
                value={editModal.form.type}
                options={NODE_TYPE_OPTIONS}
                onChange={(e) =>
                  setEditModal((prev) => ({
                    ...prev,
                    form: { ...prev.form, type: e.target.value },
                  }))
                }
              />
            </>
          ) : (
            <>
              <div className="mb-3">
                <label
                  className={`block ${t.text.secondary} text-xs font-medium mb-1`}
                >
                  Source
                </label>
                <div
                  className={`text-xs px-3 py-2 rounded ${t.input.bg} border ${t.input.border} ${t.text.primary}`}
                >
                  {editModal.form.source}
                </div>
              </div>
              <div className="mb-3">
                <label
                  className={`block ${t.text.secondary} text-xs font-medium mb-1`}
                >
                  Target
                </label>
                <div
                  className={`text-xs px-3 py-2 rounded ${t.input.bg} border ${t.input.border} ${t.text.primary}`}
                >
                  {editModal.form.target}
                </div>
              </div>
              <ModalDropdown
                t={t}
                label="Relationship"
                value={editModal.form.relationship}
                options={linkTypes}
                onChange={(e) =>
                  setEditModal((prev) => ({
                    ...prev,
                    form: { ...prev.form, relationship: e.target.value },
                  }))
                }
              />
            </>
          )}
          <ModalTextarea
            t={t}
            label="Context"
            placeholder="Add description or notes..."
            value={editModal.form.context}
            onChange={(e) =>
              setEditModal((prev) => ({
                ...prev,
                form: { ...prev.form, context: e.target.value },
              }))
            }
          />
          <ModalToggle
            t={t}
            label="Private"
            checked={editModal.form.isPrivate}
            onChange={(v) =>
              setEditModal((prev) => ({
                ...prev,
                form: { ...prev.form, isPrivate: v },
              }))
            }
          />
          <ModalActions
            t={t}
            onCancel={() => setEditModal(null)}
            onConfirm={handleEditConfirm}
            confirmLabel="Save Changes"
            disabled={
              editModal.kind === "node"
                ? !editModal.form.name.trim() || !editModal.form.context?.trim()
                : !editModal.form.relationship.trim() ||
                  !editModal.form.context?.trim()
            }
          />
        </FloatingPanel>
      )}

      {deleteModal && (
        <FloatingPanel
          t={t}
          dark={dark}
          title={deleteModal.kind === "node" ? "Delete Node" : "Delete Edge"}
          initialX={deleteModal.screenX}
          initialY={deleteModal.screenY}
        >
          <div
            className={`mb-3 px-3 py-3 rounded ${t.badge.bg} border ${t.badge.border}`}
          >
            {deleteModal.kind === "node" ? (
              <span className={`${t.text.secondary} text-xs`}>
                Are you sure you want to delete{" "}
                <strong className={t.text.primary}>
                  {deleteModal.item.name}
                </strong>
                ? This will also remove any manually created edges connected to
                this node.
              </span>
            ) : (
              <span className={`${t.text.secondary} text-xs`}>
                Are you sure you want to delete the edge between{" "}
                <strong className={t.text.primary}>
                  {typeof deleteModal.item.source === "object"
                    ? deleteModal.item.source.id
                    : deleteModal.item.source}
                </strong>
                {" → "}
                <strong className={t.text.primary}>
                  {typeof deleteModal.item.target === "object"
                    ? deleteModal.item.target.id
                    : deleteModal.item.target}
                </strong>
                ?
              </span>
            )}
          </div>
          <div className="flex items-center justify-end gap-2">
            <button
              className={`px-4 py-1.5 rounded text-xs font-medium border ${t.btn.secondary} transition-colors`}
              onClick={() => setDeleteModal(null)}
            >
              Cancel
            </button>
            <button
              className="px-4 py-1.5 rounded text-xs font-medium bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30 transition-colors"
              onClick={handleDeleteConfirm}
            >
              Delete
            </button>
          </div>
        </FloatingPanel>
      )}

      {showNewNodeModal && (
        <FloatingPanel
          t={t}
          dark={dark}
          title="Create New Node"
          initialX={newNodeModalPos.x}
          initialY={newNodeModalPos.y}
        >
          <div
            className={`mb-3 px-3 py-2 rounded ${t.badge.bg} border ${t.badge.border}`}
          >
            <span className={`${t.text.secondary} text-xs`}>
              Connecting from:{" "}
            </span>
            <span className={`${t.text.accent} text-xs font-medium`}>
              {newNodeSourceNode?.name}
            </span>
          </div>
          <ModalInput
            t={t}
            label="Node Name"
            placeholder="Enter node name..."
            value={newNodeForm.name}
            onChange={(e) =>
              setNewNodeForm((f) => ({ ...f, name: e.target.value }))
            }
            onKeyDown={(e) => {
              if (e.key === "Enter") handleCreateNewNode();
              if (e.key === "Escape") {
                setShowNewNodeModal(false);
                setNewNodeSourceNode(null);
              }
            }}
          />
          <ModalDropdown
            t={t}
            label="Node Type"
            value={newNodeForm.type}
            options={NODE_TYPE_OPTIONS}
            onChange={(e) =>
              setNewNodeForm((f) => ({ ...f, type: e.target.value }))
            }
          />
          <ModalDropdown
            t={t}
            label="Relationship"
            value={newNodeForm.relationship}
            options={linkTypes}
            onChange={(e) =>
              setNewNodeForm((f) => ({ ...f, relationship: e.target.value }))
            }
          />
          <ModalTextarea
            t={t}
            label="Node Context"
            placeholder="Describe this node..."
            value={newNodeForm.context}
            onChange={(e) =>
              setNewNodeForm((f) => ({ ...f, context: e.target.value }))
            }
          />
          <ModalTextarea
            t={t}
            label="Edge Context"
            placeholder="Describe this connection..."
            value={newNodeForm.edgeContext}
            onChange={(e) =>
              setNewNodeForm((f) => ({ ...f, edgeContext: e.target.value }))
            }
          />
          <ModalToggle
            t={t}
            label="Private"
            checked={newNodeForm.isPrivate}
            onChange={(v) => setNewNodeForm((f) => ({ ...f, isPrivate: v }))}
          />
          <ModalActions
            t={t}
            onCancel={() => {
              setShowNewNodeModal(false);
              setNewNodeSourceNode(null);
            }}
            onConfirm={handleCreateNewNode}
            confirmLabel="Create & Connect"
            disabled={
              !newNodeForm.name.trim() ||
              !newNodeForm.context.trim() ||
              !newNodeForm.edgeContext.trim()
            }
          />
        </FloatingPanel>
      )}

      {/* ── Main content ── */}
      {isGraphEmpty ? (
        <div className="relative z-10 flex w-full h-full items-center justify-center">
          <div className={`${t.empty} text-sm font-extralight`}>
            No graph data available
          </div>
        </div>
      ) : (
        <>
          {isLoading ? (
            <div className="relative z-10 flex w-full h-full items-center justify-center">
              <LoadingSpinner />
            </div>
          ) : (
            <>
              {/* ── Stats panel (top-left) ── */}
              <AnimatePresence>
                <StatsPanel
                  dark={dark}
                  t={t}
                  graphStats={graphStats}
                  backendTotals={backendTotals}
                  tooltipNode={tooltipNode}
                  isDraggingFromPlus={isDraggingFromPlus}
                  truncate={truncate}
                  formatType={formatType}
                />
              </AnimatePresence>

              {/* ── Legend panel (top-right) ── */}
              <AnimatePresence>
                <LegendPanel
                  dark={dark}
                  t={t}
                  typeCounts={typeCounts}
                  activeTypeFilter={activeTypeFilter}
                  setActiveTypeFilter={setActiveTypeFilter}
                />
              </AnimatePresence>

              {/* ── SVG overlay: drag line ── */}
              {connectDrag && dragLine && (
                <svg
                  className="absolute inset-0 z-30 pointer-events-none"
                  width="100%"
                  height="100%"
                >
                  <defs>
                    <linearGradient
                      id="connect-line-grad"
                      x1="0%"
                      y1="0%"
                      x2="100%"
                      y2="100%"
                    >
                      <stop
                        offset="0%"
                        stopColor={t.dragLine.from}
                        stopOpacity={t.dragLine.fromOpacity}
                      />
                      <stop
                        offset="100%"
                        stopColor={t.dragLine.to}
                        stopOpacity={t.dragLine.toOpacity}
                      />
                    </linearGradient>
                  </defs>
                  <line
                    x1={dragLine.x1}
                    y1={dragLine.y1}
                    x2={dragLine.x2}
                    y2={dragLine.y2}
                    stroke="url(#connect-line-grad)"
                    strokeWidth="2"
                    strokeDasharray="6 3"
                    strokeLinecap="round"
                  />
                  {!connectTargetNode &&
                    (() => {
                      const gr = is3D
                        ? 14
                        : Math.max(CIRCLE_RADIUS * zoomRef.current * 0.8, 12);
                      const cx = dragLine.x2,
                        cy = dragLine.y2;
                      return (
                        <>
                          <circle
                            cx={cx}
                            cy={cy}
                            r={gr}
                            fill={
                              dark
                                ? "rgba(15,23,42,0.5)"
                                : "rgba(255,255,255,0.5)"
                            }
                            stroke={
                              dark
                                ? "rgba(148,163,184,0.35)"
                                : "rgba(100,116,139,0.30)"
                            }
                            strokeWidth="1.5"
                            strokeDasharray="4 3"
                          />
                          <line
                            x1={cx - gr * 0.35}
                            y1={cy}
                            x2={cx + gr * 0.35}
                            y2={cy}
                            stroke={
                              dark
                                ? "rgba(148,163,184,0.5)"
                                : "rgba(100,116,139,0.45)"
                            }
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                          <line
                            x1={cx}
                            y1={cy - gr * 0.35}
                            x2={cx}
                            y2={cy + gr * 0.35}
                            stroke={
                              dark
                                ? "rgba(148,163,184,0.5)"
                                : "rgba(100,116,139,0.45)"
                            }
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                          <text
                            x={cx}
                            y={cy + gr + 12}
                            textAnchor="middle"
                            fill={
                              dark
                                ? "rgba(148,163,184,0.5)"
                                : "rgba(100,116,139,0.45)"
                            }
                            fontSize="10"
                            fontFamily="poppins"
                          >
                            New node
                          </text>
                        </>
                      );
                    })()}
                  {connectTargetNode &&
                    (() => {
                      const canvasRect = divRef.current
                        ?.querySelector("canvas")
                        ?.getBoundingClientRect();
                      const wrapperRect =
                        divRef.current?.getBoundingClientRect();
                      if (!canvasRect || !wrapperRect || !fgRef.current)
                        return null;
                      const ts = fgRef.current.graph2ScreenCoords(
                        connectTargetNode.x,
                        connectTargetNode.y,
                        connectTargetNode.z || 0,
                      );
                      const tx = ts.x + (canvasRect.left - wrapperRect.left);
                      const ty = ts.y + (canvasRect.top - wrapperRect.top);
                      let r;
                      if (is3D && fgRef.current?.camera) {
                        const p = fgRef.current.camera().position;
                        const cdx = p.x - (connectTargetNode.x || 0),
                          cdy = p.y - (connectTargetNode.y || 0),
                          cdz = p.z - (connectTargetNode.z || 0);
                        r =
                          Math.max(
                            8,
                            (CIRCLE_RADIUS * 0.72 * 800) /
                              Math.sqrt(cdx * cdx + cdy * cdy + cdz * cdz),
                          ) + 6;
                      } else {
                        r = CIRCLE_RADIUS * zoomRef.current + 6;
                      }
                      return (
                        <>
                          <circle
                            cx={tx}
                            cy={ty}
                            r={r + 4}
                            fill="none"
                            stroke="rgba(14,165,233,0.25)"
                            strokeWidth="2"
                          >
                            <animate
                              attributeName="r"
                              values={`${r + 2};${r + 8};${r + 2}`}
                              dur="1.2s"
                              repeatCount="indefinite"
                            />
                            <animate
                              attributeName="opacity"
                              values="0.6;0.2;0.6"
                              dur="1.2s"
                              repeatCount="indefinite"
                            />
                          </circle>
                          <circle
                            cx={tx}
                            cy={ty}
                            r={r}
                            fill="none"
                            stroke="rgba(14,165,233,0.5)"
                            strokeWidth="2"
                            strokeDasharray="4 3"
                          />
                          <circle
                            cx={tx}
                            cy={ty}
                            r={4}
                            fill="rgba(14,165,233,0.8)"
                          />
                        </>
                      );
                    })()}
                </svg>
              )}

              {/* ── SVG overlay: selection box ── */}
              {selectionBox &&
                selectionBox.moved &&
                (() => {
                  const wr = divRef.current?.getBoundingClientRect();
                  if (!wr) return null;
                  const x =
                    Math.min(selectionBox.startX, selectionBox.currentX) -
                    wr.left;
                  const y =
                    Math.min(selectionBox.startY, selectionBox.currentY) -
                    wr.top;
                  const w = Math.abs(
                    selectionBox.currentX - selectionBox.startX,
                  );
                  const h = Math.abs(
                    selectionBox.currentY - selectionBox.startY,
                  );
                  return (
                    <svg
                      className="absolute inset-0 z-30 pointer-events-none"
                      width="100%"
                      height="100%"
                    >
                      <rect
                        x={x}
                        y={y}
                        width={w}
                        height={h}
                        fill={t.surface.selectionBoxFill}
                        stroke={t.surface.selectionBoxStroke}
                        strokeWidth="1"
                        strokeDasharray="4 2"
                        rx="2"
                      />
                    </svg>
                  );
                })()}

              {/* ── Lazy renderer ── */}
              <Suspense
                fallback={
                  <div className="relative z-10 flex w-full h-full items-center justify-center">
                    <LoadingSpinner />
                  </div>
                }
              >
                <motion.div
                  key={is3D ? "3d" : "2d"}
                  className="w-full h-full"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5, ease: "easeOut" }}
                >
                  {is3D ? (
                    <GraphRenderer3D {...rendererProps} />
                  ) : (
                    <GraphRenderer2D {...rendererProps} />
                  )}
                </motion.div>
              </Suspense>
            </>
          )}
        </>
      )}

      {/* ── Bottom control bar ── */}
      <AnimatePresence>
        <GraphBottomBar
          t={t}
          dark={dark}
          datePickerTheme={datePickerTheme}
          is3D={is3D}
          setIs3D={setIs3D}
          graphSourceLabel={graphSourceLabel}
          graphSourceMeta={graphSourceMeta}
          activeGraphUuid={activeGraphUuid}
          connectedTypes={connectedTypes}
          teamMembers={teamMembers}
          customViews={customViews}
          switchToMyGraph={switchToMyGraph}
          switchToTeamGraph={switchToTeamGraph}
          switchToGlobalGraph={switchToGlobalGraph}
          handleControlsSelect={handleControlsSelect}
          applyCustomView={applyCustomView}
          isFavorited={isFavorited}
          toggleFavorite={toggleFavorite}
          deleteGraphView={deleteGraphView}
          loadGraphViewsData={loadGraphViewsData}
          barSearch={barSearch}
          onSearchChange={handleBarSearchChange}
          onExecuteSearch={executeSearch}
          onClearSearch={handleClearSearch}
          onClearAllFilters={clearAllFilters}
          searchMatchedNodes={searchMatchedNodes}
          showEdgeLabels={showEdgeLabels}
          setShowEdgeLabels={setShowEdgeLabels}
          manualOnly={manualOnly}
          setManualOnly={setManualOnly}
          hideOrphans={hideOrphans}
          setHideOrphans={setHideOrphans}
          degreeHighlight={degreeHighlight}
          setDegreeHighlight={setDegreeHighlight}
          graphDateFrom={graphDateFrom}
          graphDateTo={graphDateTo}
          setGraphDateFrom={setGraphDateFrom}
          setGraphDateTo={setGraphDateTo}
          graphLimit={graphLimit}
          setGraphLimit={setGraphLimit}
          refetchWithLimit={refetchWithLimit}
          graphStats={graphStats}
          backendTotals={backendTotals}
          handleZoomToFit={handleZoomToFit}
          handleRelayout={handleRelayout}
          handleZoomToNode={handleZoomToNode}
          onOpenCreateView={() => {
            setEditingView(null);
            setShowCreateViewModal(true);
          }}
          onOpenEditView={(view) => {
            setEditingView(view);
            setShowCreateViewModal(true);
          }}
        />
      </AnimatePresence>

      {showTutorial && (
        <GraphTutorial
          dark={dark}
          onClose={() => {
            setShowTutorial(false);
            setTutorialStep(0);
            if (tutorialGraphSnapshotRef.current) {
              setGraphData(tutorialGraphSnapshotRef.current);
              tutorialGraphSnapshotRef.current = null;
            }
          }}
          onStepChange={setTutorialStep}
          graphState={{
            hoveredNode: hoveredNodeRef.current,
            selectedNodeIds,
            showStandaloneModal,
            showNewNodeModal,
            connectDrag,
            showEdgeLabels,
            manualOnly,
            hideOrphans,
            degreeHighlight,
            activeTypeFilter,
            barSearch,
            graphSourceOpen: false,
            graphLimit,
            zoomLevel: zoomRef.current,
            nodeCount: graphData?.nodes?.length || 0,
            linkCount: graphData?.links?.length || 0,
          }}
        />
      )}

      {showCreateViewModal && (
        <CreateEditViewModal
          t={t}
          dark={dark}
          initialView={editingView}
          connectedTypes={connectedTypes}
          graphSources={graphSources}
          graphTargetTypes={graphTargetTypes}
          onClose={() => {
            setShowCreateViewModal(false);
            setEditingView(null);
          }}
          onCreated={applyCustomView}
          loadGraphViewsData={loadGraphViewsData}
        />
      )}
    </div>
  );
};

export default MainGraph;
